import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectOption } from '../ui/select';
import { Badge } from '../ui/badge';
import { formatCurrency } from '../../lib/utils';
import { 
  Eye, 
  Edit, 
  Trash2,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  User,
  Building2,
  DollarSign,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

const ProjectTable = ({ projects, onDelete }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [salesFilter, setSalesFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Helper function to get project status
  const getProjectStatus = (project) => {
    const paidAmount = project.totalPaidAmount || 0;
    const totalInstallmentAmount = project.totalPaymentAmount || 0;
    
    if (totalInstallmentAmount === 0) {
      return 'pending';
    }
    
    if (paidAmount >= totalInstallmentAmount) {
      return 'completed';
    }
    
    if (paidAmount > 0) {
      return 'in-progress';
    }
    
    return 'pending';
  };

  // Get unique values for filters
  const uniqueClients = useMemo(() => {
    const clients = [...new Set(projects.map(p => p.client_name).filter(Boolean))];
    return clients.sort();
  }, [projects]);

  const uniqueSales = useMemo(() => {
    const sales = [...new Set(projects.map(p => p.sales_name).filter(Boolean))];
    return sales.sort();
  }, [projects]);

  // Filter and sort projects
  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects.filter(project => {
      // Search filter
      const matchesSearch = !searchTerm || 
        project.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.sales_name && project.sales_name.toLowerCase().includes(searchTerm.toLowerCase()));

      // Status filter
      const projectStatus = getProjectStatus(project);
      const matchesStatus = statusFilter === 'all' || projectStatus === statusFilter;

      // Client filter
      const matchesClient = clientFilter === 'all' || project.client_name === clientFilter;

      // Sales filter
      const matchesSales = salesFilter === 'all' || project.sales_name === salesFilter;

      return matchesSearch && matchesStatus && matchesClient && matchesSales;
    });

    // Sort projects
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'project_name':
          aValue = a.project_name || '';
          bValue = b.project_name || '';
          break;
        case 'client_name':
          aValue = a.client_name || '';
          bValue = b.client_name || '';
          break;
        case 'sales_name':
          aValue = a.sales_name || '';
          bValue = b.sales_name || '';
          break;
        case 'start_date':
          aValue = a.start_date ? new Date(a.start_date) : new Date(0);
          bValue = b.start_date ? new Date(b.start_date) : new Date(0);
          break;
        case 'end_date':
          aValue = a.end_date ? new Date(a.end_date) : new Date(0);
          bValue = b.end_date ? new Date(b.end_date) : new Date(0);
          break;
        case 'project_price':
          aValue = parseFloat(a.project_price) || 0;
          bValue = parseFloat(b.project_price) || 0;
          break;
        case 'totalPaidAmount':
          aValue = parseFloat(a.totalPaidAmount) || 0;
          bValue = parseFloat(b.totalPaidAmount) || 0;
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [projects, searchTerm, statusFilter, clientFilter, salesFilter, sortField, sortDirection]);

  // Pagination calculations
  const totalItems = filteredAndSortedProjects.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProjects = filteredAndSortedProjects.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, clientFilter, salesFilter, sortField, sortDirection]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'warning';
      case 'pending': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'เสร็จสิ้น';
      case 'in-progress': return 'กำลังดำเนินการ';
      case 'pending': return 'รอดำเนินการ';
      default: return 'ไม่ทราบสถานะ';
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4 text-blue-600" /> : 
      <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  const handleView = (project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleEdit = (project) => {
    navigate(`/projects/${project.id}/edit`);
  };

  const handleDelete = (project) => {
    onDelete(project);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Building2 className="h-5 w-5" />
            <span>รายการโปรเจค</span>
            <Badge variant="secondary" className="ml-2">
              {totalItems} โปรเจค
            </Badge>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600">แสดง:</span>
              <Select 
                value={itemsPerPage.toString()} 
                onValueChange={(value) => setItemsPerPage(parseInt(value))}
                className="w-20"
              >
                <SelectOption value="5">5</SelectOption>
                <SelectOption value="10">10</SelectOption>
                <SelectOption value="20">20</SelectOption>
                <SelectOption value="50">50</SelectOption>
                <SelectOption value="100">100</SelectOption>
              </Select>
              <span className="text-sm font-medium text-gray-600">รายการต่อหน้า</span>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="ค้นหาชื่อโปรเจค, ลูกค้า, หรือ Sale..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 text-sm"
            />
          </div>
          
          {/* Filter Controls */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">สถานะ</label>
              <Select value={statusFilter} onValueChange={setStatusFilter} className="h-9">
                <SelectOption value="all">ทั้งหมด</SelectOption>
                <SelectOption value="completed">เสร็จสิ้น</SelectOption>
                <SelectOption value="in-progress">กำลังดำเนินการ</SelectOption>
                <SelectOption value="pending">รอดำเนินการ</SelectOption>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">ลูกค้า</label>
              <Select value={clientFilter} onValueChange={setClientFilter} className="h-9">
                <SelectOption value="all">ทั้งหมด</SelectOption>
                {uniqueClients.map(client => (
                  <SelectOption key={client} value={client}>{client}</SelectOption>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Sale</label>
              <Select value={salesFilter} onValueChange={setSalesFilter} className="h-9">
                <SelectOption value="all">ทั้งหมด</SelectOption>
                {uniqueSales.map(sales => (
                  <SelectOption key={sales} value={sales}>{sales}</SelectOption>
                ))}
              </Select>
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-semibold text-gray-700 text-sm">
                  <button
                    onClick={() => handleSort('project_name')}
                    className="flex items-center space-x-2 hover:text-blue-600 transition-colors"
                  >
                    <span>ชื่อโปรเจค</span>
                    {getSortIcon('project_name')}
                  </button>
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 text-sm">
                  <button
                    onClick={() => handleSort('client_name')}
                    className="flex items-center space-x-2 hover:text-blue-600 transition-colors"
                  >
                    <User className="h-4 w-4" />
                    <span>ลูกค้า</span>
                    {getSortIcon('client_name')}
                  </button>
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 text-sm">
                  <button
                    onClick={() => handleSort('sales_name')}
                    className="flex items-center space-x-2 hover:text-blue-600 transition-colors"
                  >
                    <span>Sale</span>
                    {getSortIcon('sales_name')}
                  </button>
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 text-sm">
                  <button
                    onClick={() => handleSort('start_date')}
                    className="flex items-center space-x-2 hover:text-blue-600 transition-colors"
                  >
                    <Calendar className="h-4 w-4" />
                    <span>วันที่เริ่ม</span>
                    {getSortIcon('start_date')}
                  </button>
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 text-sm">
                  <button
                    onClick={() => handleSort('end_date')}
                    className="flex items-center space-x-2 hover:text-blue-600 transition-colors"
                  >
                    <Calendar className="h-4 w-4" />
                    <span>วันที่จบ</span>
                    {getSortIcon('end_date')}
                  </button>
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 text-sm">
                  <button
                    onClick={() => handleSort('project_price')}
                    className="flex items-center space-x-2 hover:text-blue-600 transition-colors"
                  >
                    <DollarSign className="h-4 w-4" />
                    <span>ราคาโปรเจค</span>
                    {getSortIcon('project_price')}
                  </button>
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 text-sm">
                  <button
                    onClick={() => handleSort('totalPaidAmount')}
                    className="flex items-center space-x-2 hover:text-blue-600 transition-colors"
                  >
                    <TrendingUp className="h-4 w-4" />
                    <span>ชำระแล้ว</span>
                    {getSortIcon('totalPaidAmount')}
                  </button>
                </th>
                <th className="text-center p-3 font-semibold text-gray-700 text-sm">สถานะ</th>
                <th className="text-center p-3 font-semibold text-gray-700 text-sm">การดำเนินการ</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProjects.map((project) => {
                const status = getProjectStatus(project);
                const progressPercentage = project.totalPaymentAmount > 0 
                  ? Math.round((project.totalPaidAmount / project.totalPaymentAmount) * 100) 
                  : 0;

                return (
                  <tr key={project.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-3">
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{project.project_name}</div>
                        <div className="text-xs text-gray-500">#{project.id.slice(-6)}</div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-900">{project.client_name}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-900">{project.sales_name || '-'}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-900">
                        {project.start_date ? new Date(project.start_date).toLocaleDateString('th-TH') : '-'}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-900">
                        {project.end_date ? new Date(project.end_date).toLocaleDateString('th-TH') : '-'}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(project.project_price || 0)}
                      </div>
                    </td>
                    <td className="p-3">
                      <div>
                        <div className="text-sm font-semibold text-green-600">
                          {formatCurrency(project.totalPaidAmount || 0)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {progressPercentage}% ของยอดรวม
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant={getStatusColor(status)} className="text-xs">
                        {getStatusText(status)}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(project)}
                          className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 transition-colors"
                          title="ดูรายละเอียด"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(project)}
                          className="h-7 w-7 p-0 text-green-600 hover:bg-green-50 transition-colors"
                          title="แก้ไข"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(project)}
                          className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 transition-colors"
                          title="ลบ"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-4">
          {paginatedProjects.map((project) => {
            const status = getProjectStatus(project);
            const progressPercentage = project.totalPaymentAmount > 0 
              ? Math.round((project.totalPaidAmount / project.totalPaymentAmount) * 100) 
              : 0;

            return (
              <Card key={project.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm">{project.project_name}</h3>
                      <p className="text-xs text-gray-500">#{project.id.slice(-6)}</p>
                    </div>
                    <Badge variant={getStatusColor(status)} className="text-xs">
                      {getStatusText(status)}
                    </Badge>
                  </div>

                  {/* Client & Sales */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500">ลูกค้า</p>
                      <p className="font-medium text-gray-900">{project.client_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Sale</p>
                      <p className="font-medium text-gray-900">{project.sales_name || '-'}</p>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500">วันที่เริ่ม</p>
                      <p className="font-medium text-gray-900">
                        {project.start_date ? new Date(project.start_date).toLocaleDateString('th-TH') : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">วันที่จบ</p>
                      <p className="font-medium text-gray-900">
                        {project.end_date ? new Date(project.end_date).toLocaleDateString('th-TH') : '-'}
                      </p>
                    </div>
                  </div>

                  {/* Financial Info */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500">ราคาโปรเจค</p>
                      <p className="font-semibold text-gray-900">{formatCurrency(project.project_price || 0)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">ชำระแล้ว</p>
                      <p className="font-semibold text-green-600">{formatCurrency(project.totalPaidAmount || 0)}</p>
                      <p className="text-gray-500">{progressPercentage}% ของยอดรวม</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-1 pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleView(project)}
                      className="h-7 px-2 text-blue-600 hover:bg-blue-50 text-xs"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      ดู
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(project)}
                      className="h-7 px-2 text-green-600 hover:bg-green-50 text-xs"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      แก้ไข
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(project)}
                      className="h-7 px-2 text-red-600 hover:bg-red-50 text-xs"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      ลบ
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 border-t pt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              {/* Pagination Info */}
              <div className="text-sm text-gray-700">
                แสดง <span className="font-semibold">{startIndex + 1}</span> ถึง <span className="font-semibold">{Math.min(endIndex, totalItems)}</span> จาก <span className="font-semibold">{totalItems}</span> รายการ
              </div>
              
              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                {/* First Page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0"
                  title="หน้าแรก"
                >
                  <ChevronsLeft className="h-3 w-3" />
                </Button>
                
                {/* Previous Page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0"
                  title="หน้าก่อน"
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const maxVisible = 5;
                    const pages = [];
                    
                    if (totalPages <= maxVisible) {
                      // Show all pages if total is small
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Show pages around current page
                      let start = Math.max(1, currentPage - 2);
                      let end = Math.min(totalPages, currentPage + 2);
                      
                      // Adjust if we're near the beginning or end
                      if (currentPage <= 3) {
                        end = Math.min(totalPages, 5);
                      } else if (currentPage >= totalPages - 2) {
                        start = Math.max(1, totalPages - 4);
                      }
                      
                      for (let i = start; i <= end; i++) {
                        pages.push(i);
                      }
                    }
                    
                    return pages.map(pageNum => (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={`h-8 w-8 p-0 text-xs font-medium ${
                          currentPage === pageNum 
                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </Button>
                    ));
                  })()}
                </div>

                {/* Next Page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="h-8 w-8 p-0"
                  title="หน้าถัดไป"
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
                
                {/* Last Page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="h-8 w-8 p-0"
                  title="หน้าสุดท้าย"
                >
                  <ChevronsRight className="h-3 w-3" />
                </Button>
              </div>
            </div>
            
            {/* Page Info */}
            <div className="mt-4 text-center text-xs text-gray-500">
              หน้า {currentPage} จาก {totalPages} หน้า
            </div>
          </div>
        )}

        {totalItems === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Building2 className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || statusFilter !== 'all' || clientFilter !== 'all' || salesFilter !== 'all' 
                ? 'ไม่พบโปรเจคที่ตรงกับเงื่อนไข' 
                : 'ยังไม่มีโปรเจค'}
            </h3>
            <p className="text-gray-500">
              {searchTerm || statusFilter !== 'all' || clientFilter !== 'all' || salesFilter !== 'all'
                ? 'ลองเปลี่ยนคำค้นหาหรือตัวกรอง' 
                : 'เริ่มต้นด้วยการเพิ่มโปรเจคแรกของคุณ'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectTable;
